<!-- eslint-disable vue/html-self-closing -->
<template>
  <div class="gf-file-blob">
    <div
      v-if="Object.keys(blob).length > 0"
      class="gf-file-blob__header"
    >
      <div class="gf-file-blob__header-file">
        <span class="gf-file-blob__header-file__name">
          <i class="gf-icon gf-icon-file mr-1" />
          <span class="text-dark text-truncate">{{ blob.fileName }}</span>
        </span>

        <!-- LFS and Lock logic -->
        <span
          v-if="blob?.lfsOid || blob?.lockedBy"
          class="project-badge mt-1"
        >
          <span
            :class="{ 'mr-1': blob?.lockedBy }"
            v-if="blob?.lfsOid"
          >
            LFS
          </span>
          <span
            v-if="blob?.lockedBy"
            :title="`${$t('file.file_locked_by')} : @${blob.lockedBy}`"
          >
            <i
              class="gf-icon gf-icon-lock gf-fs-1"
              :class="{ 'mb-1': !blob?.lfsOid }"
            ></i>
          </span>
        </span>
        <!-- LFS and Lock logic -->

        <span class="gf-file-blob__header-file__info">
          <small
            v-if="isReadableFile"
            class="mr-2"
          >
            строк {{ blob.lines && Object.keys(blob.lines).length }}
          </small>
          <small>{{ blob.prettyBlobSize }}</small>
        </span>
      </div>
      <div class="gf-file-blob__header-actions">
        <div
          v-if="isReadableFile"
          class="gf-file-blob__header-actions-markdown btn-group btn-group-sm"
          role="group"
        >
          <button
            class="btn btn-outline-secondary text-secondary"
            :title="$t('file.code')"
            :style="{
              'background-color':
                previewMode === 'code' ? 'var(--bg-light-selected)' : 'var(--white)'
            }"
            @click="onCodeClick"
          >
            <span :style="{ color: previewMode === 'code' ? 'var(--text)' : 'var(--text-muted)' }">
              Code
            </span>
          </button>
          <button
            class="btn btn-outline-secondary text-secondary"
            title="Blame"
            :style="{
              'background-color':
                previewMode === 'blame' ? 'var(--bg-light-selected)' : 'var(--white)'
            }"
            @click="onBlameClick"
          >
            <span :style="{ color: previewMode === 'blame' ? 'var(--text)' : 'var(--text-muted)' }">
              Blame
            </span>
          </button>
          <button
            v-if="blob?.fileExtension === 'md'"
            class="btn btn-outline-secondary text-secondary"
            :title="$t('file.preview')"
            :style="{
              'background-color':
                previewMode === 'markdown' ? 'var(--bg-light-selected)' : 'var(--white)'
            }"
            @click="onMarkdownPreviewClick"
          >
            <span
              :style="{ color: previewMode === 'markdown' ? 'var(--text)' : 'var(--text-muted)' }"
            >
              Preview
            </span>
          </button>
        </div>
        <div
          v-if="isReadableFile && previewMode !== 'markdown'"
          class="gf-file-blob__header-actions-wrapping btn-group btn-group-sm"
          role="group"
        >
          <button
            class="d-flex align-items-center btn btn-outline-secondary text-secondary"
            :title="$t('file.doNotWrapLines')"
            :style="{
              'background-color': !isTextWrap ? 'var(--bg-light-selected)' : 'var(--white)'
            }"
            @click="onNotTextWrapClick"
          >
            <i
              class="gf-icon gf-icon-arrows-left-right"
              :style="{ color: !isTextWrap ? 'var(--text)' : 'var(--text-muted)' }"
            />
          </button>
          <button
            class="d-flex align-items-center btn btn-outline-secondary text-secondary"
            :title="$t('file.wrapLines')"
            :style="{
              'background-color': isTextWrap ? 'var(--bg-light-selected)' : 'var(--white)'
            }"
            @click="onTextWrapClick"
          >
            <i
              class="gf-icon gf-icon-arrow-turn-down"
              :style="{ color: isTextWrap ? 'var(--text)' : 'var(--text-muted)' }"
            />
          </button>
        </div>
        <div
          class="gf-file-blob__header-actions-file btn-group btn-group-sm"
          role="group"
        >
          <a
            v-if="isReadableFile"
            class="btn btn-outline-secondary text-white bg-white"
            :title="$t('file.raw')"
            :href="fileBlobFileRaw"
            target="_blank"
          >
            Raw
          </a>
          <!-- <button
            v-if="isReadableFile"
            class="btn btn-outline-secondary text-secondary bg-white"
            title="Скопировать"
          >
            <i class=" gf-icon gf-icon-copy" />
          </button> -->
          <a
            class="d-flex align-items-center btn btn-outline-secondary text-white bg-white"
            :title="$t('file.download')"
            :href="fileBlobDownloadLink"
          >
            <i class="gf-icon gf-icon-download" />
          </a>
        </div>
        <div
          class="gf-file-blob__header-actions-edit ml-3"
          v-if="hasUserRole"
        >
          <gf-custom-tooltip
            v-if="canEditFile"
            width="200px"
            class="d-inline-block font-weight-normal"
            position="left"
          >
            <template #element>
              <button
                class="d-flex align-items-center btn btn-sm btn-dark"
                type="button"
                :title="$t('file.edit')"
                :disabled="!hasEditPermissions || !lockedByTheSameUser || blob?.lfsOid"
                @click="onFileEditClick"
              >
                <i class="gf-icon gf-icon-pen"></i>
              </button>
            </template>
            <template
              v-if="!hasEditPermissions || !lockedByTheSameUser || blob?.lfsOid"
              #content
            >
              <small>{{ cannotEditText }}</small>
            </template>
          </gf-custom-tooltip>
          <gf-custom-tooltip
            width="200px"
            class="d-inline-block font-weight-normal"
            position="left"
          >
            <template #element>
              <button
                class="d-flex align-items-center btn btn-sm btn-danger"
                type="button"
                :title="$t('file.delete')"
                :disabled="!canDeleteFile || !lockedByTheSameUser || blob?.lfsOid"
                @click="openDeleteFileModal"
              >
                <i class="gf-icon gf-icon-trash"></i>
              </button>
            </template>
            <template
              v-if="!canDeleteFile || !lockedByTheSameUser || blob?.lfsOid"
              #content
            >
              <small>{{ cannotDeleteText }}</small>
            </template>
          </gf-custom-tooltip>
        </div>
      </div>
      <gf-custom-modal-window
        v-if="hasUserRole && canDeleteFile"
        ref="gfCustomModalWindowDeleteFile"
        class="text-dark"
        gf-modal-data-name="deleteFileModal"
        :gf-modal-header="$t('file.deleteQuestion')"
        gf-modal-wrapper-width="500px"
        @close="resetErrors"
      >
        <template #body>
          <project-delete-file-form
            action-type="delete"
            :branch="branch"
            :file-name="getFileName"
            :is-last-file-in-folder="isLastFileInFolder"
            :folder-name="parentFolderName"
            @cancel="closeDeleteFileModal"
            @submit="deleteFile"
          />
        </template>
      </gf-custom-modal-window>
    </div>
    <div
      v-else
      class="gf-file-blob__header --preloader"
    />
    <div
      v-if="previewMode === 'blame'"
      class="gf-file-blob__blame-header"
    >
      <div class="gf-file-blob__blame-header-colors">
        <span class="small">{{ $t('commit.older') }}</span>
        <div class="gf-file-blob__blame-header-colors-blocks">
          <div
            v-for="(period, index) in timePeriods"
            :key="index"
            class="gf-file-blob__blame-header-colors-blocks-item"
            :style="{ 'background-color': period.color }"
          ></div>
        </div>
        <span class="small">{{ $t('commit.newer') }}</span>
      </div>
      <!-- <div class="gf-file-blob__blame-header-contributors">Коммитеры</div> -->
    </div>
    <div
      v-if="Object.keys(blob).length > 0 && blob?.blobSize === 0"
      class="gf-file-blob__blob-empty py-2 px-3"
    >
      Файл пустой
    </div>
    <div
      v-else-if="Object.keys(blob).length > 0"
      class="gf-file-blob__blob"
    >
      <div
        v-if="blob.binary && blob.img"
        class="gf-file-blob__blob-image"
      >
        <img
          class="mw-90 border"
          :src="fileBlobFileRaw"
        />
      </div>
      <div
        v-else-if="blob.binary || blob.large"
        class="gf-file-blob__blob-download"
      >
        <a
          class="d-flex align-items-center text-center text-muted text-decoration-none"
          :href="fileBlobDownloadLink"
        >
          <i class="gf-icon gf-icon-file-download" />
          <h4 class="mt-2">{{ $t('file.download') }} {{ blob.prettyBlobSize }}</h4>
        </a>
      </div>
      <div v-else-if="blob.fileExtension === 'md' && previewMode === 'markdown'">
        <div
          class="p-3 markdown-body"
          v-html="markdownPreview"
        ></div>
      </div>
      <!-- previewMode !== 'markdown' || -->
      <div v-else-if="previewMode !== 'markdown' || (previewMode === 'blame' && blame?.linesBlame)">
        <table
          class="gf-file-blob__blob-code table table-sm mb-0 table-borderless"
          :style="codeTableStyle"
        >
          <tr
            v-for="(line, index) in blob.lines"
            :id="'line-num-' + (index + 1)"
            :key="index"
            class="chunk-operation-none"
            :class="
              previewMode === 'blame' && blame?.linesBlame && showBlames[index] && index > 0
                ? 'border-light-top'
                : ''
            "
            :set="
              blame?.linesBlame
                ? ((blameLine = blameLinesInfo[index]),
                  (blameColor = getBlameColor(blameLine?.commitTime)))
                : ((blameLine = null), (blameColor = null))
            "
          >
            <td
              v-if="
                previewMode === 'blame' &&
                Object.keys(blameLinesInfo).length > 0 &&
                showBlames[index]
              "
              class="gf-file-blob__blob-code-blame"
              :rowspan="blameLine?.lastLine - blameLine?.firstLine + 1 || 1"
              :style="{ '--blame-date-color': blameColor }"
            >
              <div
                v-if="blameLine"
                class="gf-file-blob__blob-code-blame-content"
              >
                <span class="commit-time">
                  {{ $moment(blameLine?.commitTime * 1000).fromNow() }}
                </span>
                <div class="gf-file-blob__blob-code-blame-content-commit">
                  <gf-custom-tooltip
                    class="committer-avatar"
                    width="max-content"
                    :position="index === 0 ? 'bottom' : 'top'"
                    :element-html="`
                    <a
                        href='
                          ${
                            blameLine?.committer
                              ? '/user/' + blameLine?.committer?.username
                              : 'mailto:' + blameLine?.committerEmail
                          }'
                      >
                        <img
                          class='rounded-circle'
                          height='20px'
                          width='20px'
                          src='${
                            blameLine?.committer
                              ? blameLine?.committer?.avatar
                              : '/static/image/avatar.jpg'
                          }'
                          loading='lazy'
                        />
                      </a>`"
                    :content-html="
                      blameLine?.committer
                        ? `<span>${blameLine?.committer?.fullName}</span>`
                        : `<span>${blameLine?.committerName}</span>`
                    "
                  ></gf-custom-tooltip>
                  <a
                    :href="`/project/${alias}/${projectAlias}/commit/${blameLine?.commitId}`"
                    class="commit-message text-truncate"
                  >
                    {{ blameLine?.commitMessage }}
                  </a>
                </div>
                <gf-custom-tooltip
                  v-if="blameLine?.priorChangeCommitId"
                  class="prior"
                  :position="index <= 3 ? 'bottom' : 'top'"
                  :element-html="`
                    <a
                      v-if='blameLine'
                      type='button'
                      class='btn btn-sm prior-link'
                      href='${getPriorCommitHref(blameLine?.priorChangeCommitId)}'
                    >
                      <i class=' gf-icon gf-icon-arrow-up'></i>
                    </a>`"
                  :content-html="`<small>
                    ${$t('blame.prior.message', {
                      commitHash: blameLine?.priorChangeCommitId?.slice(0, 7)
                    })}</small>
                  `"
                ></gf-custom-tooltip>
              </div>
            </td>
            <td
              v-else-if="previewMode === 'blame' && !blameLine"
              class="gf-file-blob__blob-code-blame"
            >
              <packman-loader
                v-if="index === 0"
                size="15"
                style="top: 15%"
              />
            </td>

            <td class="chunk-num no-select text-secondary p-0">
              <a
                class="d-block text-right text-dark pl-3 pr-1"
                :href="'#line-num-' + (index + 1)"
              >
                {{ index + 1 }}
              </a>
            </td>
            <td
              class="code-line py-0 w-100"
              :class="$route?.hash === '#line-num-' + (index + 1) ? 'highlighted' : 'bg-white'"
              :style="{ 'white-space': isTextWrap ? 'normal' : 'nowrap' }"
              v-html="line"
            />
          </tr>
        </table>
      </div>
    </div>
    <div
      v-else
      class="gf-file-blob__blob --preloader"
    >
      <packman-loader
        size="15"
        :style="{ top: '35%' }"
      />
    </div>
  </div>
</template>

<script>
import RepoS from '@shared/services/repository.service';
import { FORM_NAME_ENUMS } from '@shared/variables/form-name.enums';
import PackmanLoader from '@shared/shared-components/shared/packman-loader';
import GfCustomTooltip from '@shared/shared-components/shared/GfCustomTooltip';
import GfCustomModalWindow from '@shared/shared-components/shared/GfCustomModalWindow';
import projectFilesMessages from '@/shared-modules/modules/project-files/project-files.messages';
// eslint-disable-next-line max-len
import projectDeleteFileForm from '@/shared-modules/modules/project-files/components/project-delete-file-form';
import qs from 'qs';
import { messageNotification } from '@shared/services/gf.notification.service';

export default {
  name: 'GfFileBlob',
  components: {
    PackmanLoader,
    GfCustomTooltip,
    GfCustomModalWindow,
    projectDeleteFileForm
  },
  props: {
    alias: {
      type: String,
      required: true
    },
    projectAlias: {
      type: String,
      required: true
    },
    commitHash: {
      type: String,
      required: true
    },
    filePath: {
      type: String,
      required: true
    },
    blob: {
      type: Object,
      default() {
        return {};
      },
      required: true
    },
    markdownPreview: {
      type: String,
      default: null
    },
    blame: {
      type: Object,
      default() {
        return {};
      }
    },
    permissions: {
      type: Object,
      default() {
        return {};
      }
    },
    hasEditPermissions: {
      type: Boolean,
      default: false
    },
    hasUserRole: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isTextWrap: false,
      isMarkdownPreview: false,
      previewMode: 'code',
      lastBlameIndex: 0,
      blameLinesInfo: {},
      showBlames: {},
      timePeriods: [
        {
          startDate: this.$moment().subtract(2, 'year'),
          color: 'var(--gf-time-block-1)'
        },
        {
          startDate: this.$moment().subtract(1, 'year'),
          color: 'var(--gf-time-block-2)'
        },
        {
          startDate: this.$moment().subtract(6, 'month'),
          color: 'var(--gf-time-block-3)'
        },
        {
          startDate: this.$moment().subtract(1, 'month'),
          color: 'var(--gf-time-block-4)'
        },
        {
          startDate: this.$moment().subtract(2, 'week'),
          color: 'var(--gf-time-block-5)'
        },
        {
          startDate: this.$moment().subtract(1, 'week'),
          color: 'var(--gf-time-block-6)'
        },
        {
          startDate: this.$moment().subtract(1, 'day'),
          color: 'var(--gf-time-block-7)'
        },
        {
          startDate: this.$moment(),
          color: 'var(--gf-time-block-8)'
        }
      ]
    };
  },
  computed: {
    fileBlobFileRaw() {
      let encodedBlobPath = encodeURIComponent(this.blob.path);
      let encodedCommitHash = encodeURIComponent(this.commitHash);
      return `/project/${this.alias}/${this.projectAlias}/blob/raw?file=${encodedBlobPath}&commit=${encodedCommitHash}`;
    },
    fileBlobDownloadLink() {
      let encodedBlobPath = encodeURIComponent(this.blob.path);
      let encodedCommitHash = encodeURIComponent(this.commitHash);
      return `/project/${this.alias}/${this.projectAlias}/blob/raw?file=${encodedBlobPath}&inline=false&commit=${encodedCommitHash}`;
    },
    isReadableFile() {
      return !this.blob.binary && !this.blob.img && !this.blob.large;
    },
    codeTableStyle() {
      if (!this.isTextWrap) return { display: 'block', overflow: 'auto' };
      return {};
    },
    routerPreviewMode() {
      return this.$route?.query?.mode || 'code';
    },
    cannotEditText() {
      return this.$route.query.commit
        ? this.$t('file.switchToBranchToEditFile')
        : !this.lockedByTheSameUser
        ? this.$t('file.blockedByAnotherUser')
        : this.blob?.lfsOid
        ? this.$t('file.lfsIsNotEditable')
        : this.$t('file.notEnoughPermissionsToEditFile');
    },
    cannotDeleteText() {
      return this.$route.query.commit
        ? this.$t('file.switchToBranchToDeleteFile')
        : !this.lockedByTheSameUser
        ? this.$t('file.blockedByAnotherUser')
        : this.blob?.lfsOid
        ? this.$t('file.lfsIsNotEditable')
        : this.$t('file.notEnoughPermissionsToDeleteFile');
    },
    canEditFile() {
      return this.isReadableFile;
    },
    canDeleteFile() {
      return this.hasEditPermissions;
    },
    getFileName() {
      return this.filePath?.split('/')?.pop();
    },
    branch() {
      return this.$route.query?.branch;
    },
    isLastFileInFolder() {
      return (
        this.$store.getters.getNodeChildrenByPath(
          this.filePath?.substring(0, this.filePath.lastIndexOf('/')) || ''
        )?.length === 1 && this.parentFolderName !== undefined
      );
    },
    parentFolderName() {
      const foldersArray = this.filePath?.split('/');
      foldersArray.pop();
      return foldersArray.pop();
    },
    lockedByTheSameUser() {
      return this.blob.lockedBy ? this.$store.getters.getUserAlias === this.blob.lockedBy : true;
    },
    isLfs() {
      return !!this.blob.lfsOid;
    }
  },
  watch: {
    blob() {
      this.previewMode = 'code';
      if (this.blob.fileExtension === 'md' && this.routerPreviewMode === 'code')
        this.$router.push({ query: { ...this.$route.query, mode: 'markdown' } });
      this.updateRouteMode();
      if (this.previewMode === 'blame') this.getBlameInfo();
    },
    '$route.query.mode': function () {
      this.updateRouteMode();
    },
    blame() {
      if (this.blob) {
        this.getBlameInfo();
      }
    }
  },
  mounted() {
    this.updateRouteMode();
    if (!this.getFileTextWrapCookie()) {
      this.setFileTextWrapCookie(false);
    } else {
      this.setFileTextWrapCookie(this.getFileTextWrapCookie());
      this.isTextWrap = this.getFileTextWrapCookie() === 'true';
    }
  },
  methods: {
    updateRouteMode() {
      if (this.isReadableFile) {
        if (this.routerPreviewMode && this.routerPreviewMode !== this.previewMode) {
          switch (this.routerPreviewMode) {
            case 'code':
              this.onCodeClick();
              break;
            case 'blame':
              this.onBlameClick();
              break;
            case 'markdown':
              this.onMarkdownPreviewClick();
              break;
            default:
              break;
          }
        }
      }
    },
    setFileTextWrapCookie(isTextWrap) {
      document.cookie = `isFileTextWrap=${isTextWrap}; expires=${new Date(
        new Date().getTime() + 1000 * 60 * 60 * 24 * 365
      ).toGMTString()}; path=/`;
    },
    getFileTextWrapCookie() {
      function escape(s) {
        // eslint-disable-next-line no-useless-escape
        return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1');
      }
      const match = document.cookie.match(RegExp(`(?:^|;\\s*)${escape('isFileTextWrap')}=([^;]*)`));
      return match ? match[1] : null;
    },
    onNotTextWrapClick() {
      this.isTextWrap = false;
      this.setFileTextWrapCookie(false);
    },
    onTextWrapClick() {
      this.isTextWrap = true;
      this.setFileTextWrapCookie(true);
    },
    onCodeClick() {
      this.previewMode = 'code';
      if (this.routerPreviewMode !== 'code') {
        this.$router.push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            mode: undefined
          }
        });
      }
    },
    onBlameClick() {
      this.previewMode = 'blame';
      if ((!this.blame && this.blob?.blobSize > 0) || Object.keys(this.blob).length === 0) {
        this.$emit('blame');
      }
      if (this.routerPreviewMode !== 'blame') {
        this.$router.push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            mode: 'blame'
          }
        });
      }
    },
    onMarkdownPreviewClick() {
      this.previewMode = 'markdown';
      if (!this.markdownPreview) this.$emit('markdown');
      if (this.routerPreviewMode !== 'markdown') {
        this.$router.push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            mode: 'markdown'
          }
        });
      }
    },
    onFileEditClick() {
      if (this.canEditFile) {
        this.$router.push({
          name: 'project-file-edit',
          query: this.$route.query
        });
      }
    },
    openDeleteFileModal() {
      this.$refs.gfCustomModalWindowDeleteFile.openModalWindow();
    },
    closeDeleteFileModal() {
      this.$refs.gfCustomModalWindowDeleteFile.closeModalWindow();
      this.resetErrors();
    },
    deleteFile(data) {
      RepoS.deleteFile(this.alias, this.projectAlias, {
        commitHash: this.commitHash,
        fileName: this.getFileName,
        filePath: encodeURIComponent(
          this.filePath?.substring(0, this.filePath.lastIndexOf('/')) || ''
        ),
        commitMessage: data.form.message,
        sourceBranch: this.branch,
        targetBranch: data.form.isDirectCommit ? this.branch : data.form.newBranchName,
        projectAlias: this.projectAlias,
        ownerAlias: this.alias
      })
        .then((newData) => {
          this.closeDeleteFileModal();
          const encodedParams = qs.stringify({
            file: newData?.redirectUrl,
            branch: newData?.params?.targetBranch
          });
          window.location.href = `/project/${this.alias}/${this.projectAlias}/file?${encodedParams}`;
        })
        .catch((err) => {
          if (err?.response?.data?.status === 403) {
            this.closeDeleteFileModal();
            messageNotification('danger', {
              message: err.response.data.detail,
              hasTimer: false
            });
          }
        });
    },
    getPriorCommitHref(priorCommitId) {
      return `/project/${this.alias}/${this.projectAlias}/blob?file=${this.blob?.path}&commit=${priorCommitId}&mode=blame`;
    },
    getBlameInfo() {
      this.blameLinesInfo = {};
      this.showBlames = {};
      this.lastBlameIndex = 0;
      let blameIsAdded = false;
      let startIndex = 0;
      for (let i = 0; i < this.blob?.lines?.length; i++) {
        startIndex = this.lastBlameIndex;
        for (let j = startIndex; j < this.blame?.linesBlame?.length; j++) {
          const currentBlameLine = this.blame.linesBlame[j];
          if (i >= currentBlameLine.firstLine && i <= currentBlameLine.lastLine) {
            this.showBlames[i] = !blameIsAdded;
            this.blameLinesInfo[i] = currentBlameLine;
            blameIsAdded = true;
            break;
          } else {
            blameIsAdded = false;
            this.lastBlameIndex += 1;
          }
        }
      }
    },
    getBlameColor(commitTime) {
      if (!commitTime) return 'var(--white)';
      const momentDate = this.$moment(commitTime * 1000);

      // eslint-disable-next-line no-restricted-syntax
      for (const period of this.timePeriods) {
        const startDate = this.$moment(period.startDate);
        if (startDate && momentDate.isSameOrBefore(startDate)) return period.color;
      }
      return 'var(--white)';
    },
    resetErrors() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.DELETE_FILE_FORM,
        errorResponse: null
      });
    }
  },
  i18n: { messages: projectFilesMessages }
};
</script>

<style lang="scss">
$this: '.gf-file-blob';

#{$this} {
  position: relative;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  #{$this}__header {
    top: 0;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: var(--text-muted);
    background-color: vgf-file-blob__header-filear(--light);
    border-bottom: 1px solid var(--border-color);

    @media screen and (max-width: 460px) {
      justify-content: center;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    @media screen and (min-width: 461px) and (max-width: 543px) {
      align-items: flex-start;
    }

    &-file {
      display: flex;
      overflow: auto;
      white-space: nowrap;
      align-items: center;
      gap: 8px;

      @media screen and (max-width: 543px) {
        flex-direction: column;
      }

      &__name {
        display: flex;
        align-items: center;
        overflow: auto;
        white-space: nowrap;
      }
    }

    &-actions {
      display: flex;
      align-items: center;
      gap: 4px;

      &-markdown {
        font-family: 'JetBrains Mono';
      }

      &-file {
        font-family: 'JetBrains Mono';
      }

      &-edit {
        display: flex;
        gap: 4px;
      }

      @media screen and (max-width: 543px) {
        flex-wrap: wrap;
      }
    }

    &.--preloader {
      height: 46px;
    }

    &-actions-markdown * {
      padding: 0.1rem 0.3rem;
    }
    &-actions-wrapping * {
      padding: 0.1rem 0.3rem;
    }
    &-actions-file * {
      padding: 0.1rem 0.3rem;
    }
    &-actions-edit button {
      padding: 0.2rem 0.5rem;
    }
  }
  #{$this}__blame-header {
    padding: 6px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-color);

    &-colors {
      display: flex;
      align-items: center;
      gap: 6px;

      &-blocks {
        display: flex;
        gap: 1px;

        &-item {
          width: 10px;
          height: 10px;
        }
      }
    }
  }
  #{$this}__blob {
    position: relative;

    &-empty {
      text-align: center;
    }

    &-download,
    &-image {
      padding: 16px;
      display: flex;
      justify-content: center;
      background-color: var(--light);
    }

    &-code {
      overflow-wrap: anywhere;

      &-blame {
        --blame-date-color: var(--white);
        min-width: 300px;
        max-width: 300px;
        padding: 2px 4px 2px 12px;
        position: relative;
        white-space: nowrap;

        @media screen and (max-width: 767px) {
          min-width: 150px;
          max-width: 150px;
        }

        &::before {
          content: '';
          height: 100%;
          width: 6px;
          top: 0;
          left: 0;
          position: absolute;
          display: block;
          background-color: var(--blame-date-color);
          border: 1px solid var(--white);
          border-radius: 4px;
        }

        &-content {
          position: relative;
          display: flex;
          align-items: center;
          gap: 5px;

          @media screen and (max-width: 767px) {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          &-commit {
            width: calc(100% - 142px);
            display: flex;
            gap: 5px;

            @media screen and (max-width: 767px) {
              width: 100%;
            }

            & > .committer-avatar {
              display: inline-block;
            }
            & > .commit-message {
              width: 100%;
              color: var(--text);
              font-size: 13px;
            }
          }

          & > .commit-time {
            width: 100px;
            color: var(--text-muted);
            font-size: 12px;
          }

          & > .prior {
            margin-left: auto;

            @media screen and (max-width: 767px) {
              position: absolute;
              top: 0;
              right: 0;
            }

            & .prior-link {
              color: var(--text-muted);
              padding: 0.1rem 0.3rem;

              &:hover {
                color: var(--text);
                background-color: var(--bg-light-selected);
              }

              @media screen and (max-width: 767px) {
                padding: 0 0.3rem;
              }
            }
          }
        }
      }
    }

    &.--preloader {
      position: relative;
      height: 46px;
    }

    .border-light-top {
      border-top: 1px solid var(--gf-border-color-light) !important;
    }
  }

  .markdown-body {
    word-break: break-word;
    & table {
      width: 100%;
      table-layout: fixed;
      & th,
      & tr {
        min-width: 250px;
        max-width: 500px;
        word-wrap: break-word;
      }
    }
  }
}
</style>
